import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { t } from "i18next";
import { tokenHelper } from "../stores/helpers";
import { useUsageStore } from "../stores/Usage";
import { useAuth } from "../stores/Auth/Auth";
import { canFullOrder, canShowReportItem, REPORT_MENU_ITEMS } from "../helpers/planLimits";

const Navdata = () => {
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [isCurrentState, setIsCurrentState] = useState("Dashboard");
  const [activeStates, setActiveStates] = useState({
    Dashboard: false,
    Assets: false,
    SupAssets: false,
    Orders: false,
    Settings: false,
  });

  useEffect(() => {
    const fetchUserAndPermissions = async () => {
      const user = await tokenHelper.getUser();
      const perms = await tokenHelper.getPermissions();
      setCurrentUser(user);

      setPermissions(perms || {});
    };
    fetchUserAndPermissions();
  }, []);

  const { usage, fetchUsage, permissions: livePermissions, hasActiveSubscription: liveSubscription } = useUsageStore();
  const user = useAuth((state) => state.user);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  useEffect(() => {
    if (user) setCurrentUser(user);
  }, [user]);

  useEffect(() => {
    if (livePermissions) setPermissions(livePermissions);
  }, [livePermissions]);

  const fullOrderEnabled = canFullOrder(usage) || (currentUser?.role ?? user?.role) === "ROOT";
  const effectivePermissions = livePermissions ?? permissions ?? {};
  const effectiveSubscription =
    liveSubscription !== null
      ? liveSubscription === true
      : currentUser?.hasActiveSubscription === true || user?.hasActiveSubscription === true;

  const updateIconSidebar = (e) => {
    const target = e?.target;
    if (target?.getAttribute("subitems")) {
      document.querySelectorAll(".nav-icon.active").forEach((item) => {
        item.classList.remove("active");
        const id = item.getAttribute("subitems");
        document.getElementById(id)?.classList.remove("show");
      });
    }
  };

  useEffect(() => {
    setActiveStates((prev) => ({
      ...Object.fromEntries(Object.keys(prev).map((key) => [key, false])),
      [isCurrentState]: true,
    }));
  }, [isCurrentState]);

  // Permission check
  const hasPermission = (key, level = 1) => Number(effectivePermissions?.[key] ?? 0) >= level;
  const isRoot = (currentUser?.role ?? user?.role) === "ROOT";

  const menuItems = useMemo(() => {
    const assetsSubItems = [
      (hasPermission("kitchen") || isRoot) && fullOrderEnabled && {
        id: "Kitchens",
        label: "Service Stations",
        link: "/kitchens",
        parentId: "Assets",
      },
      (hasPermission("myspaces") || isRoot) && {
        id: "MySpace",
        label: "Spaces",
        link: "/myspaces",
        parentId: "Assets",
      },
      (hasPermission("mymenus") || isRoot) && {
        id: "MyMenus",
        label: "Menus",
        link: "/mymenus",
        parentId: "Assets",
      },
      (hasPermission("menu") || isRoot) && {
        id: "Menus",
        label: "Menus",
        link: "/menus",
        parentId: "Assets",
      },
      (hasPermission("site") || isRoot) && {
        id: "Sites",
        label: "Branches",
        link: "/sites",
        parentId: "Assets",
      },
      (hasPermission("space") || isRoot) && {
        id: "Spaces",
        label: "Spaces",
        link: "/spaces",
        parentId: "Assets",
      },
    ].filter(Boolean);

    const assetsSection =
      assetsSubItems.length > 0
        ? {
            id: "Assets",
            label: "My Assets",
            icon: "ri-map-pin-2-fill",
            link: "",
            stateVariables: activeStates.Assets,
            click: (e) => {
              e.preventDefault();
              setIsCurrentState("Assets");
              updateIconSidebar(e);
            },
            subItems: assetsSubItems,
          }
        : null;

    const reportsSubItems = REPORT_MENU_ITEMS.map(
      (item) =>
        canShowReportItem(isRoot, effectivePermissions, item.permission, effectiveSubscription) && item
    ).filter(Boolean);

    const reportsSection =
      reportsSubItems.length > 0
        ? {
            id: "Reports",
            label: "Reports",
            icon: "ri-ticket-2-line",
            link: "",
            stateVariables: activeStates.Orders,
            click: (e) => {
              e.preventDefault();
              setIsCurrentState("Orders");
              updateIconSidebar(e);
            },
            subItems: reportsSubItems,
          }
        : null;

    const settingsSubItems = [
      (hasPermission("link") || isRoot) && {
        id: "Manage Team",
        label: "Manage Team",
        link: "/manage-team",
        parentId: "Settings",
      },
      isRoot && {
        id: "Links",
        label: "Links",
        link: "/links",
        parentId: "Settings",
      },
      (hasPermission("device") || isRoot) && {
        id: "Clickers",
        label: "Clickers",
        link: "/clickers",
        icon: "ri-device-line",
        parentId: "Settings",
      },
      (hasPermission("announcements") || isRoot) && {
        id: "Announcements",
        label: "Announcements",
        link: "/announcements",
        parentId: "Settings",
      },
      (hasPermission("setting") || isRoot) && {
        id: "Splash",
        label: "Splash",
        link: "/settings/splash",
        parentId: "Settings",
      },
      isRoot && {
        id: "AppVersion",
        label: "App Version",
        link: "/settings/app-version",
        parentId: "Settings",
      },
      isRoot && {
        id: "Users",
        label: "Users",
        link: "/staffs",
        parentId: "Settings",
      },
      isRoot && {
        id: "PeriodicReport",
        label: "Periodic Report Email",
        link: "/report/periodic-email",
        parentId: "Settings",
      },
    ].filter(Boolean);

    const settingsSection =
      settingsSubItems.length > 0
        ? {
            id: "Settings",
            label: "Settings",
            icon: "ri-settings-2-line",
            link: "/settings",
            stateVariables: activeStates.Settings,
            click: (e) => {
              e.preventDefault();
              setIsCurrentState("Settings");
              updateIconSidebar(e);
            },
            subItems: settingsSubItems,
          }
        : null;

    return [
      { label: t("Menu"), isHeader: true },

      {
        id: "dashboard",
        label: "Dashboard",
        icon: "ri-dashboard-2-line",
        link: "/dashboard",
        stateVariables: activeStates.Dashboard,
        click: (e) => {
          e.preventDefault();
          setIsCurrentState("Dashboard");
          updateIconSidebar(e);
        },
      },

      assetsSection,

      reportsSection,

      isRoot && {
        id: "SupAssets",
        label: "Super Admin",
        icon: "ri-map-pin-2-fill",
        link: "",
        stateVariables: activeStates.SupAssets,
        click: (e) => {
          e.preventDefault();
          setIsCurrentState("SupAssets");
          updateIconSidebar(e);
        },
        subItems: [
          {
            id: "Sites",
            label: "Branches",
            link: "/sites",
            icon: "ri-building-4-fill",
            parentId: "SupAssets",
          },
          {
            id: "Kitchens",
            label: "Service Stations",
            link: "/kitchens",
            parentId: "SupAssets",
          },
          {
            id: "KitchenTablets",
            label: "Station Tablets",
            link: "/kitchen-tablets",
            icon: "ri-device-line",
            parentId: "SupAssets",
          },
          {
            id: "Menus",
            label: "Menus",
            link: "/menus",
            parentId: "SupAssets",
          },
          {
            id: "Spaces",
            label: "Spaces",
            link: "/spaces",
            parentId: "SupAssets",
          },
        ].filter(Boolean),
      },

      settingsSection,
    ].filter(Boolean);
  }, [activeStates, currentUser, permissions, usage, user, fullOrderEnabled, livePermissions, liveSubscription, effectivePermissions, effectiveSubscription]);

  return <>{menuItems}</>;
};

export default Navdata;
